import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Determine base URL based on environment
// In development: use /api (proxied by Vite dev server)
// In production: use full production URL with /api
const baseUrl = "/api";

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const sessionId = getState().auth.sessionId;
    if (sessionId) {
      headers.set("guestSessionId", sessionId);
    }
    // Don't set content-type — fetchBaseQuery handles it automatically.

    // Session ids are credentials — never log them outside development.
    if (import.meta.env.DEV && endpoint === "registerGuestFCMToken") {
      console.log("🔔 FCM token registration request prepared");
    }

    if (import.meta.env.DEV) {
      // console.log(`API Request [${endpoint}]:`, {
      //   sessionId: sessionId || "none",
      //   headers: Object.fromEntries(headers.entries()),
      // });
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  try {
    // Check if session is expired before making the request
    const state = api.getState();
    const { sessionExpiry, hasActiveSession } = state.auth;

    if (hasActiveSession && sessionExpiry) {
      const now = new Date();
      const expiry = new Date(sessionExpiry);

      if (now >= expiry) {
        // Session expired, dispatch session expired action
        // console.log("Session expired, clearing session...");
        api.dispatch({ type: "auth/sessionExpired" });
        return {
          error: {
            status: 401,
            data: "Session expired. Please scan the QR code again.",
            originalArgs: args,
          },
        };
      }
    }

    if (import.meta.env.DEV) {
      // console.log("Making API request:", {
      //   url: typeof args === "string" ? args : args.url,
      //   method: typeof args === "string" ? "GET" : args.method || "GET",
      //   hasSession: !!state.auth.sessionId,
      // });
    }

    let result = await baseQuery(args, api, extraOptions);

    // Safely handle undefined result
    if (!result) {
      console.warn("Base query returned undefined result");
      return {
        error: {
          status: "FETCH_ERROR",
          data: "No response received",
          originalArgs: args,
        },
      };
    }

    const isNotificationTokenRequest =
      api.endpoint === "registerGuestFCMToken" ||
      api.endpoint === "removeGuestFCMToken";

    // Notification registration is optional. Its failure must not end a valid
    // guest session while the app is still moving from the splash to Home.
    if (result?.error?.status === 401) {
      const state = api.getState();
      const { hasActiveSession, sessionId } = state.auth;

      if (import.meta.env.DEV) {
        // console.log("401 Unauthorized response:", {
        //   hasActiveSession,
        //   hasSessionId: !!sessionId,
        //   url: typeof args === "string" ? args : args.url,
        //   response: result.error,
        // });
      }

      // Only clear session if we actually had an active session
      // This prevents clearing session during initial setup/loading
      if (hasActiveSession && sessionId && !isNotificationTokenRequest) {
        api.dispatch({ type: "auth/sessionExpired" });
        // Drop every cached response tied to the dead session. Without this the
        // previous guest's folio, requests and cart stay in the RTK Query cache
        // (keepUnusedDataFor is 5 minutes) and can still render after the
        // session is gone.
        api.dispatch(apiSlice.util.resetApiState());
      }
    }

    // A 403 here means the hotel was disabled, its subscription lapsed, or the
    // session belongs to a different hotel than the route. None of those are
    // recoverable by retrying, and previously none of them were handled at all.
    if (result?.error?.status === 403) {
      const { hasActiveSession, sessionId } = api.getState().auth;
      const message =
        typeof result.error.data === "object" && result.error.data
          ? result.error.data.message
          : result.error.data;

      if (
        hasActiveSession &&
        sessionId &&
        typeof message === "string" &&
        message.toLowerCase().includes("does not belong to this hotel")
      ) {
        api.dispatch({ type: "auth/sessionExpired" });
        api.dispatch(apiSlice.util.resetApiState());
      }
    }

    return result;
  } catch (error) {
    console.error("API query error:", error);
    return {
      error: {
        status: "FETCH_ERROR",
        data: error?.message || "An error occurred during the request",
        originalArgs: args,
      },
    };
  }
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Session",
    "Menu",
    "Order",
    "Service",
    "Hotel",
    "FnbCart",
    "HousekeepingCart",
    "Conversation",
    "Request",
    "Folio",
    "HotelFeatures",
  ],
  endpoints: () => ({}),
  keepUnusedDataFor: 5 * 60, // Keep unused data for 5 minutes
  refetchOnFocus: false, // Don't refetch when window regains focus
  refetchOnReconnect: true, // Refetch when network reconnects
});

// Export API slice
export default apiSlice;
